
import {translate} from '@elements/translations';
import LoadingSpinner from '~vue/components/loading/LoadingSpinner.vue';
import FullscreenModal from '~vue/components/modals/FullscreenModal.vue';
import StepNav from '~vue/components/step-nav/StepNav.vue';
import RegionView from '~vue/views/ticket-views/RegionView.vue';
import TicketTypeView from '~vue/views/ticket-views/TicketTypeView.vue';
import PersonsView from '~vue/views/ticket-views/PersonsView.vue';
import CalendarView from '~vue/views/ticket-views/CalendarView.vue';
import {defineComponent, ref, watch, onMounted, onBeforeMount} from 'vue';
import {useTicketStore} from '~vue/stores/TicketStore.ts';
import {storeToRefs} from 'pinia'
import axios from "axios";
import SuccessView from "./ticket-views/SuccessView.vue";
import DateListView from "./ticket-views/DateListView.vue";
import {getAddToCartUrl, getWarningUrl} from "../utils/urls.ts";
// @ts-ignore
import {resultTracking} from "@elements/tracking";
import Toast from 'bootstrap/js/dist/toast';
import {findIn, findAllIn} from "@elements/dom-utils";
import {initInScope} from "@elements/init-modules-in-scope";

export default defineComponent({
    name: "TicketConfig",
    components: {
        DateListView,
        SuccessView,
        LoadingSpinner,
        FullscreenModal,
        StepNav,
        RegionView,
        TicketTypeView,
        CalendarView,
        PersonsView
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        initialView: {
            type: String,
            required: true
        },
        hasCalendarView: {
            type: Boolean,
            required: true,
        },
        hasDateListView: {
            type: Boolean,
            required: true,
        },
        buttonConfig: {
            type: Object,
            required: false,
            default: {
                text: translate('shop.ticket.configuration.add-to-cart'),
                class: "btn btn-primary"
            }
        },
        defaultCatalogId: {
            type: String,
            required: false
        },
        defaultTicketId: {
            type: String,
            required: false
        },
        defaultRegion: {
            type: Object,
            required: false
        },
        defaultTicketTypeId: {
            type: String,
            required: false
        },
        defaultTicketSubTypeId: {
            type: String,
            required: false
        },
        defaultTicketName: {
            type: String,
            required: false
        },
        type: {
            type: String,
            required: false,
            default: 'ticket'
        },
    },
    setup(props) {
        // initialize stores
        const ticketStore = useTicketStore();
        const {
            step,
            selectedRegion,
            selectedCatalogId,
            selectedTicketSubType,
            selectedTicketType,
            ticketConfigurations,
            selectedTicketName,
            selectedDate,
            hasCalendarView,
            isInitialized,
            showModal
        } = storeToRefs(ticketStore);
        const {changeStep, resetAll, changeIsInitialized, changeType, changeHasCalendarView, changeRegion, changeTicketName, changeTicketSubType,changeCatalogId, changeTicketType} = ticketStore;

        const isLoading = ref<boolean>(false);
        const stepViews = ref<String[]>([]);
        const steps = ref<number[]>(4);
        const successData = ref<Object>({});

        const getStepViews = <array>() => {
            let views = [];

            views.push("region");
            views.push('ticketType');

            if(hasCalendarView.value && !props.hasDateListView) {
                views.push('calendar');
            } else if(props.hasDateListView) {
                views.push('dateList');
            }

            views.push('persons');

            return views;
        };

        const fetchWarnings = async () => {
            let url = getWarningUrl(props.type);

            if(selectedCatalogId.value.length > 0) {
                url = url + '/' + selectedCatalogId.value;
            } else {
                return;
            }

            let config = {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json'
            };

            let request = axios.get(url, config);

            await request.then((result) => result.data).then((result) => {
                if(result.warnings) {
                    result.warnings.forEach((warning) => {
                        createToast({
                            type: warning.type,
                            content: warning.content,
                            id: warning.id,
                            href: warning.href,
                            link: warning.link
                        })
                    });
                }
            }).catch((error) => {
                console.warn(error);
            });
        }

        const createToast = (options: any) => {
            let container = findIn('.warning-container', document.body);
            if (container === null) {
                container = document.createElement('div');
                container.classList.add('warning-container');
                document.body.appendChild(container);
            }

            if (!sessionStorage.getItem('alert-bar-' + options.id)){
                container.insertAdjacentHTML('beforeend', (
                    `<div class="js-vue-warning js-alert toast fade show toast--${options.type}" id="${options.id}" role="alert" data-bs-autohide="false" aria-live="assertive" aria-atomic="true">
                        <div class="alert-bar__icon d-flex align-items-center justify-content-center flex-shrink-0">
                             <span class="icon icon-callsign" aria-label="warning" title="warning"></span>
                        </div>

                        ${options.content ? (`<div class="toast-body">${options.content}</div>`) : ''}
                        ${options.href ? (`<a href="${options.href}" target="_blank" class="text-decoration-underline text-nowrap px-2 alert-bar__link">${options.link}</a>`) : ''}

                        <button type="button" class="btn-close close js-alert__close" data-bs-dismiss="toast" aria-label="close" title="close"></button>
                    </div>`
                ));
            }
            else return;

            let toasts = findAllIn('.js-vue-warning', container);
            toasts.map((toast) => {
                new Toast(toast);
            });

            initInScope();
        }

        const onAddToCart = async () => {
            isLoading.value = true;

            // just use specific priceGroupId and serviceId from ticketConfigurations and make new array
            let cartConfig = ticketConfigurations.value.map((config) => {
                return {
                    priceGroupId: config.priceGroupId,
                    serviceId: config.service
                }
            });

            const params = {
                regionId: selectedRegion.id,
                startDate: selectedRegion.startDate,
                catalogId: selectedCatalogId.value,
                ticketTypeId: selectedTicketType.value,
                ticketId: selectedTicketSubType.value,
                dateStart: selectedDate.value.start,
                configurations: cartConfig
            };

            let url = getAddToCartUrl(props.type);
            let config = {
                'X-Requested-With': 'XMLHttpRequest',
                'Content-Type': 'application/json'
            };

           let request = axios.post(url, params, config);

            await request.then((result) => result.data).then((result) => {
                isLoading.value = false;
                successData.value = result;

                if(result.cartSum) {
                    if(document.getElementsByClassName('js-cart-counter')[0]) {
                        document.getElementsByClassName('js-cart-counter')[0].innerHTML = result.cartSum;
                    }
                }

                // execute resultTracking
                // data contains and executes _trackingScript
                if(result.responseTrackingData && window._trackingData) {
                    window.dataLayer = window['dataLayer'] || [];
                    window.dataLayer.push({ ecommerce: null });  // Clear the previous ecommerce object.
                    resultTracking(result);
                }

            }).catch((error) => {
                console.warn(error);
                isLoading.value = false;
                successData.value.success = false;
            });
        }

        const onCloseModal = () => {
            document.getElementsByTagName('body')[0].classList.remove('no-scroll');
            removeWarnings();
            // resetAll();
        }

        const onLoading = (loading: boolean) => {
            isLoading.value = loading;
        }

        watch(() => hasCalendarView.value, (newValue, oldValue) => {
            if(newValue !== oldValue) {
                stepViews.value = getStepViews();
            }
        });

        watch(() => step.value, (value) => {
           if(step.value === stepViews.value.length) {
               removeWarnings();
           }
        });

        const removeWarnings = () => {
            findAllIn('.warning-container', document.body).map((container) => {
                container.remove();
            });
        }

        // to reinitialize data when modal is opened (when it was already mounted before)
        watch(() => showModal.value, (newValue, oldValue) => {
            if(newValue !== oldValue && !isInitialized.value) {
                stepViews.value = getStepViews();
                const index = stepViews.value.indexOf(props.initialView);
                changeStep(index);
                initializeData();
                changeIsInitialized(true);
            }
            if (newValue === true) {
                fetchWarnings();
            }
        });

        onBeforeMount(() => {
            stepViews.value = getStepViews();
            // get index of key in object
            const index = stepViews.value.indexOf(props.initialView);
            changeStep(index);
        });

        function initializeData() {
            stepViews.value = getStepViews();

            if(props.hasCalendarView) {
                changeHasCalendarView(props.hasCalendarView);
            }
            if(props.defaultTicketName && selectedTicketType.value === "") {
                changeTicketName(props.defaultTicketName);
            }
            if(props.defaultCatalogId && props.defaultCatalogId !== "" && selectedCatalogId.value === "") {
                changeCatalogId(props.defaultCatalogId);
            }
            if(props.defaultTicketId && props.defaultTicketId !== "" && selectedTicketSubType.value === "") {
                changeTicketSubType(props.defaultTicketId);
            }
            if(props.defaultTicketTypeId && props.defaultTicketTypeId !== "" && selectedTicketType.value === "") {
                changeTicketType(props.defaultTicketTypeId);
            }
            if(props.defaultTicketSubTypeId && props.defaultTicketSubTypeId !== "" && selectedTicketSubType.value === "") {
                changeTicketSubType(props.defaultTicketSubTypeId);
            }
            if(props.defaultRegion && selectedRegion.value.id === "") {
                changeRegion("id",props.defaultRegion.id);
                changeRegion("name",props.defaultRegion.name);
                changeRegion("isMainRegion",props.defaultRegion.isMainRegion);
            }
            if(props.type && props.type !== "") {
                changeType(props.type);
            }
        }

        onMounted(() => {
            initializeData();
        });

        return {
            isLoading,
            onLoading,
            step,
            steps,
            stepViews,
            selectedRegion,
            selectedTicketName,
            onAddToCart,
            onCloseModal,
            translate,
            successData,
            fetchWarnings
        }
    }
});
